import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, Save, RefreshCw, Plus, Trash2, X, Wand2, Upload, FileText } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [savingId, setSavingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyProduct = { name: "", tagline: "", dosing: "", reconstitution: "", price: "", quantity: "", image: "", is_stack: false, ingredients: "" };
  const [newP, setNewP] = useState(emptyProduct);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genRowId, setGenRowId] = useState(null);
  const [coas, setCoas] = useState([]);
  const [coaForm, setCoaForm] = useState({ title: "", product_name: "" });
  const [coaFile, setCoaFile] = useState(null);
  const [uploadingCoa, setUploadingCoa] = useState(false);

  const uploadCoa = async (e) => {
    e.preventDefault();
    if (!coaForm.title || !coaFile) { toast.error("Title and file are required"); return; }
    setUploadingCoa(true);
    try {
      const fd = new FormData();
      fd.append("title", coaForm.title);
      fd.append("product_name", coaForm.product_name);
      fd.append("file", coaFile);
      await api.post("/coas", fd);
      toast.success("COA uploaded");
      setCoaForm({ title: "", product_name: "" }); setCoaFile(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingCoa(false); }
  };

  const deleteCoa = async (c) => {
    if (!window.confirm(`Remove COA "${c.title}"?`)) return;
    try { await api.delete(`/coas/${c.id}`); toast.success("COA removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const generateRowVial = async (p) => {
    setGenRowId(p.id);
    try {
      const { data } = await api.post("/products/generate-vial", { name: p.name });
      if (!data?.image) throw new Error("no image");
      setField(p.id, "image", data.image);
      await api.put(`/products/${p.id}`, { image: data.image });
      toast.success("Vial image updated");
    } catch { toast.error("Generation failed"); }
    finally { setGenRowId(null); }
  };

  const generateVial = async () => {
    if (!newP.name) { toast.error("Enter a product name first"); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/products/generate-vial", { name: newP.name });
      setNewP((prev) => ({ ...prev, image: data.image }));
      toast.success("Vial image generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenLoading(false); }
  };

  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill both fields"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated — use it next login");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setPwSaving(false); }
  };

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/coas").then((r) => setCoas(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setField = (id, field, value) =>
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const save = async (p) => {
    setSavingId(p.id);
    try {
      await api.put(`/products/${p.id}`, {
        name: p.name, tagline: p.tagline, dosing: p.dosing, reconstitution: p.reconstitution, image: p.image,
        price: parseFloat(p.price), quantity: parseInt(p.quantity, 10),
      });
      toast.success(`${p.name} updated`);
    } catch { toast.error("Update failed"); }
    finally { setSavingId(null); }
  };

  const create = async () => {
    if (!newP.name || !newP.price) { toast.error("Name and price are required"); return; }
    setCreating(true);
    try {
      await api.post("/products", {
        name: newP.name, tagline: newP.tagline, dosing: newP.dosing, reconstitution: newP.reconstitution,
        price: parseFloat(newP.price) || 0, quantity: parseInt(newP.quantity, 10) || 0,
        image: newP.image || "https://static.prod-images.emergentagent.com/jobs/d7cdb151-3b6d-4b6f-9ff2-fbeb6ae92f5e/images/ac0da53c08f845927a85c92658c027ae04554e01e71c9996906e466c02a464f6.jpeg",
        is_stack: newP.is_stack,
        ingredients: newP.ingredients ? newP.ingredients.split(",").map((s) => s.trim()).filter(Boolean) : [],
        order: products.length + 1,
      });
      toast.success(`${newP.name} added`);
      setNewP(emptyProduct); setShowAdd(false); load();
    } catch { toast.error("Could not add product"); }
    finally { setCreating(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Product deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#030305] text-white">
      <div className="grain" />
      <header className="border-b border-white/10 sticky top-0 bg-black/60 backdrop-blur-xl z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-display font-black tracking-tighter">POLYON<span className="text-[#00F0FF]">//</span>PEPS <span className="text-slate-500 text-xs font-mono2 ml-2">ADMIN</span></p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400 font-mono2 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#00F0FF] font-mono2" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex gap-2">
            {["products", "orders", "coas", "account"].map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-5 py-2 text-xs uppercase tracking-widest font-mono2 border ${tab === t ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "border-white/15 text-slate-400 hover:border-[#00F0FF]"}`}
                data-testid={`tab-${t}`}>
                {t} {t === "orders" && `(${orders.length})`}{t === "coas" && `(${coas.length})`}
              </button>
            ))}
          </div>
          <button onClick={load} className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#00F0FF] font-mono2" data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
        </div>

        {tab === "products" && (
          <div className="space-y-3" data-testid="admin-products">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-slate-500 font-mono2">Edit price, quantity (stock), name, best-use &amp; dosing. Click Save per row.</p>
              <button onClick={() => setShowAdd((v) => !v)}
                className="flex items-center gap-2 border border-[#00F0FF]/50 text-[#00F0FF] px-4 py-2 text-xs uppercase tracking-widest font-mono2 hover:bg-[#00F0FF] hover:text-black transition-colors" data-testid="admin-add-toggle">
                {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "Add Product"}
              </button>
            </div>

            {showAdd && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="border border-[#00F0FF]/30 bg-[#00F0FF]/[0.03] p-5 mb-4" data-testid="admin-add-form">
                <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-mono2 mb-4">New Compound</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Name *" value={newP.name} onChange={(v) => setNewP({ ...newP, name: v })} testid="new-name" />
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2">Vial Image</label>
                    <div className="flex gap-2 mt-1">
                      <input type="text" value={newP.image.startsWith("data:") ? "(generated image)" : newP.image}
                        onChange={(e) => setNewP({ ...newP, image: e.target.value })} placeholder="Paste URL or generate →"
                        className="flex-1 bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2 text-xs font-mono2" data-testid="new-image" />
                      <button type="button" onClick={generateVial} disabled={genLoading}
                        className="flex items-center gap-2 border border-[#00F0FF]/50 text-[#00F0FF] px-3 py-2 text-xs uppercase tracking-widest font-mono2 hover:bg-[#00F0FF] hover:text-black transition-colors disabled:opacity-50 whitespace-nowrap" data-testid="new-generate-vial">
                        <Wand2 size={14} /> {genLoading ? "…" : "Generate"}
                      </button>
                    </div>
                    {newP.image && (
                      <img src={newP.image} alt="preview" className="mt-2 w-16 h-20 object-cover bg-black rounded vial-glow" data-testid="new-image-preview" />
                    )}
                  </div>
                  <Field label="Best Use (one sentence)" value={newP.tagline} onChange={(v) => setNewP({ ...newP, tagline: v })} testid="new-tagline" />
                  <Field label="Dosing Protocol" value={newP.dosing} onChange={(v) => setNewP({ ...newP, dosing: v })} testid="new-dosing" />
                  <Field label="Reconstitution Guide" value={newP.reconstitution} onChange={(v) => setNewP({ ...newP, reconstitution: v })} testid="new-reconstitution" />
                  <Field label="Price ($) *" type="number" value={newP.price} onChange={(v) => setNewP({ ...newP, price: v })} testid="new-price" />
                  <Field label="Quantity (stock)" type="number" value={newP.quantity} onChange={(v) => setNewP({ ...newP, quantity: v })} testid="new-qty" />
                  <Field label="Ingredients (comma-separated, for stacks)" value={newP.ingredients} onChange={(v) => setNewP({ ...newP, ingredients: v })} testid="new-ingredients" />
                  <label className="flex items-center gap-2 self-end pb-2 text-xs font-mono2 text-slate-400">
                    <input type="checkbox" checked={newP.is_stack} onChange={(e) => setNewP({ ...newP, is_stack: e.target.checked })} data-testid="new-is-stack" />
                    This is a stacked/blend product
                  </label>
                </div>
                <button onClick={create} disabled={creating}
                  className="mt-4 flex items-center gap-2 bg-[#00F0FF] text-black px-5 py-2.5 text-xs uppercase tracking-widest font-bold font-mono2 disabled:opacity-50" data-testid="admin-create-btn">
                  <Plus size={14} /> {creating ? "Adding…" : "Add Product"}
                </button>
              </motion.div>
            )}
            {products.map((p) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="border border-white/10 bg-white/[0.02] p-4 grid md:grid-cols-[96px_1fr_auto] gap-4 items-start" data-testid={`admin-row-${p.id}`}>
                <div className="flex flex-col gap-2">
                  <img src={p.image} alt={p.name} className="w-16 h-20 object-cover bg-black rounded vial-glow" />
                  <button onClick={() => generateRowVial(p)} disabled={genRowId === p.id}
                    className="flex items-center gap-1.5 border border-[#00F0FF]/50 text-[#00F0FF] px-2 py-1.5 text-[10px] uppercase tracking-widest font-mono2 hover:bg-[#00F0FF] hover:text-black transition-colors disabled:opacity-50 justify-center" data-testid={`admin-generate-${p.id}`}>
                    <Wand2 size={12} /> {genRowId === p.id ? "…" : "Vial"}
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 w-full">
                  <Field label="Name" value={p.name} onChange={(v) => setField(p.id, "name", v)} testid={`admin-name-${p.id}`} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Price ($)" type="number" value={p.price} onChange={(v) => setField(p.id, "price", v)} testid={`admin-price-${p.id}`} />
                    <Field label="Quantity" type="number" value={p.quantity} onChange={(v) => setField(p.id, "quantity", v)} testid={`admin-qty-${p.id}`} />
                  </div>
                  <Field label="Best Use" value={p.tagline} onChange={(v) => setField(p.id, "tagline", v)} testid={`admin-tagline-${p.id}`} />
                  <Field label="Dosing" value={p.dosing} onChange={(v) => setField(p.id, "dosing", v)} testid={`admin-dosing-${p.id}`} />
                  <Field label="Reconstitution" value={p.reconstitution || ""} onChange={(v) => setField(p.id, "reconstitution", v)} testid={`admin-reconstitution-${p.id}`} />
                </div>
                <div className="flex flex-col gap-2 self-center">
                  <button onClick={() => save(p)} disabled={savingId === p.id}
                    className="flex items-center gap-2 bg-[#00F0FF] text-black px-4 py-2 text-xs uppercase tracking-widest font-bold font-mono2 disabled:opacity-50 justify-center" data-testid={`admin-save-${p.id}`}>
                    <Save size={14} /> {savingId === p.id ? "…" : "Save"}
                  </button>
                  <button onClick={() => remove(p)}
                    className="flex items-center gap-2 border border-[#FF003C]/50 text-[#FF003C] px-4 py-2 text-xs uppercase tracking-widest font-mono2 hover:bg-[#FF003C] hover:text-white transition-colors justify-center" data-testid={`admin-delete-${p.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="admin-orders">
            {orders.length === 0 && <p className="text-slate-500 font-mono2 text-sm">No inquiries yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`order-${o.id}`}>
                <div className="flex justify-between text-sm font-mono2">
                  <span className="text-[#00F0FF]">#{o.id.slice(0, 8).toUpperCase()}</span>
                  <span className="font-display font-bold">${o.total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-slate-400 font-mono2 mt-1">{o.customer_name} · {o.email} {o.phone && `· ${o.phone}`}</p>
                <p className="text-[11px] text-slate-500 font-mono2 mt-2">{o.items.map((i) => `${i.name} ×${i.qty}`).join("  •  ")}</p>
                {o.note && <p className="text-[11px] text-slate-500 font-mono2 mt-1 italic">"{o.note}"</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "coas" && (
          <div data-testid="admin-coas">
            <form onSubmit={uploadCoa} className="border border-[#00F0FF]/30 bg-[#00F0FF]/[0.03] p-5 mb-6 max-w-2xl" data-testid="coa-upload-form">
              <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-mono2 mb-4">Upload New COA (PDF, PNG, JPG, WEBP — max 15MB)</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2">Title *</label>
                  <input type="text" value={coaForm.title} onChange={(e) => setCoaForm({ ...coaForm, title: e.target.value })}
                    className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2 text-xs font-mono2" data-testid="coa-title" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2">Related Product (optional)</label>
                  <input type="text" list="coa-products" value={coaForm.product_name} onChange={(e) => setCoaForm({ ...coaForm, product_name: e.target.value })}
                    className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2 text-xs font-mono2" data-testid="coa-product" />
                  <datalist id="coa-products">
                    {products.map((p) => <option key={p.id} value={p.name} />)}
                  </datalist>
                </div>
              </div>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setCoaFile(e.target.files[0])}
                className="mt-3 w-full text-xs font-mono2 text-slate-400 file:mr-3 file:border file:border-[#00F0FF]/50 file:bg-transparent file:text-[#00F0FF] file:px-3 file:py-1.5 file:text-xs file:uppercase file:tracking-widest file:cursor-pointer" data-testid="coa-file" />
              <button type="submit" disabled={uploadingCoa}
                className="mt-4 flex items-center gap-2 bg-[#00F0FF] text-black px-5 py-2.5 text-xs uppercase tracking-widest font-bold font-mono2 disabled:opacity-50" data-testid="coa-upload-btn">
                <Upload size={14} /> {uploadingCoa ? "Uploading…" : "Upload COA"}
              </button>
            </form>

            <div className="space-y-2">
              {coas.length === 0 && <p className="text-slate-500 font-mono2 text-sm">No COAs uploaded yet.</p>}
              {coas.map((c) => (
                <div key={c.id} className="flex items-center gap-4 border border-white/10 bg-white/[0.02] p-4" data-testid={`admin-coa-${c.id}`}>
                  <FileText size={18} className="text-[#00F0FF] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono2 text-sm truncate">{c.title}</p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2 truncate">{c.product_name || "General"} · {c.original_filename}</p>
                  </div>
                  <a href={`${API}/coas/${c.id}/download`} target="_blank" rel="noopener noreferrer"
                    className="text-xs uppercase tracking-widest text-[#00F0FF] font-mono2 hover:opacity-70" data-testid={`admin-coa-view-${c.id}`}>View</a>
                  <button onClick={() => deleteCoa(c)} className="text-slate-500 hover:text-[#FF003C]" data-testid={`admin-coa-delete-${c.id}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "account" && (
          <div className="max-w-md" data-testid="admin-account">
            <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-mono2 mb-4">Change Admin Password</p>
            <div className="border border-white/10 bg-white/[0.02] p-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono2">Current Password</label>
                <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                  className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2.5 text-sm font-mono2" data-testid="pw-current" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono2">New Password (min 6 chars)</label>
                <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                  className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2.5 text-sm font-mono2" data-testid="pw-new" />
              </div>
              <button onClick={changePw} disabled={pwSaving}
                className="w-full bg-[#00F0FF] text-black py-3 text-xs uppercase tracking-widest font-bold font-mono2 disabled:opacity-50" data-testid="pw-submit">
                {pwSaving ? "Updating…" : "Update Password"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2 text-xs font-mono2" data-testid={testid} />
    </label>
  );
}
